import { useEffect, useMemo, useRef } from 'react';

import { useAuth } from '../auth/AuthContext';
import {
  syncUsedeskWidget,
  type UsedeskDocument,
  type UsedeskWindow,
} from '../lib/usedesk-bridge';
import {
  usedeskEnabled,
  usedeskIdentity,
  usedeskShouldMount,
  type UsedeskIdentity,
} from '../lib/usedesk-model';

/**
 * T.Z49 — the UseDesk support chat. T.Z116 (owner, 2026-09-17): mounted ONCE at
 * the app root (`router.tsx`'s pathless layout route, inside `<AuthProvider>`),
 * so it exists on EVERY surface the SPA renders — the authenticated shell AND
 * the unauthenticated one. A visitor on `/login` (either door) or `/logged-out`
 * boots it ANONYMOUSLY: `usedeskIdentity(null)` → `null` → no `userIdentify`
 * call is ever sent, the chat just arrives unidentified; the moment a session
 * resolves, the T.Z49 flow takes over with no change — identify with the
 * session user's own name + e-mail, re-identify on an account switch.
 *
 * Why the root and not `AppLayout` (the T.Z49 home) any more: placement WAS the
 * policy while the widget had to stay off the pre-auth screens; now the pre-auth
 * screen is the point, and the root is the single bootstrap point that makes the
 * anonymous → identified → switch → session-gone transitions a matter of the
 * `identity` prop changing, never of two components fighting over one script tag
 * (`loadUsedeskWidget` is idempotent, but one mount is one fewer thing to prove).
 *
 * OWNER, 2026-09-14 (the ISM2-6304 answer): the widget shows for ALL roles —
 * ADMIN, МЕНЕДЖЕР, КУРАТОР and a plain viewer — so nothing here reads `role`.
 *
 * Behaviour (see `syncUsedeskWidget` for the framework-free half):
 *  - the build flag (`VITE_USEDESK_ENABLED`, default off when unset) keeps the
 *    third-party script out of any build that did not ask for it — the e2e
 *    `vite build`, local dev — while the Dockerfile ARG default turns it on for
 *    the images that actually deploy;
 *  - while `/auth/me` is unresolved nothing mounts (the first paint of the
 *    widget is either a knowing anonymous one or an identified one);
 *  - anonymous: booted with NO identity; a widget that had ALREADY identified a
 *    now-dead session (the one logout that is not a page load — `ProtectedRoute`'s
 *    client-side bounce) is hidden instead, because UseDesk still holds the
 *    previous user's attribution and a new chat would be filed under their name;
 *  - ready: identified with `me.user.name` + `me.user.email` (their data, on
 *    purpose — the feature); the synthetic dev identities carry no plausible
 *    e-mail and stay unidentified;
 *  - a changed session user re-identifies (account switch).
 *
 * No unmount cleanup: the root mount only ever unmounts with the whole app (a
 * page navigation — which is also how a real logout resets the widget's DOM),
 * and a hide-on-cleanup would eat the launcher on the dev StrictMode
 * mount→cleanup→mount cycle with nothing to call `toggle(true)` back.
 *
 * Renders nothing — the widget draws its own DOM.
 */
export function UsedeskWidget() {
  const { status, user } = useAuth();
  const enabled = usedeskEnabled(import.meta.env.VITE_USEDESK_ENABLED);
  // Memoised on the query-cache user object: a fresh object per render would
  // re-run the effect on every route change for no reason. `null` while
  // unauthenticated IS the anonymous boot — it mounts, it just identifies nobody.
  const identity = useMemo(() => usedeskIdentity(user), [user]);
  /** The identity the widget is currently carrying — what makes a re-identify a change. */
  const carried = useRef<UsedeskIdentity | null>(null);

  useEffect(() => {
    // One decision, one state to remember — the whole T.Z49 + T.Z116 matrix
    // lives in `syncUsedeskWidget`, where the plain-Node suite can pin it.
    const { carried: next } = syncUsedeskWidget(window as unknown as UsedeskWindow, document as unknown as UsedeskDocument, {
      mount: usedeskShouldMount({ enabled, status }),
      identity,
      carried: carried.current,
    });
    carried.current = next;
  }, [enabled, status, identity]);

  return null;
}
